import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import '../styles/companionAppUsageReport.css';

const CHART_WIDTH = 560;
const CHART_HEIGHT = 230;
const PADDING = {
  top: 18,
  right: 24,
  bottom: 38,
  left: 34
};

const SERIES = [
  { key: 'day1Percent', label: 'Day 1', color: '#77bd86' },
  { key: 'day7Percent', label: 'Day 7', color: '#edbd64' },
  { key: 'day30Percent', label: 'Day 30', color: '#df6f70' }
];

const formatPercent = (value) => `${Number(value || 0).toFixed(0)}%`;

const formatChange = (value) => {
  const numericValue = Number(value || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toFixed(1)}% YoY`;
};

const getX = (index, total) => {
  const usableWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  if (total <= 1) {
    return PADDING.left;
  }
  return PADDING.left + ((usableWidth / (total - 1)) * index);
};

const getY = (value) => {
  const usableHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  return PADDING.top + usableHeight - ((Number(value || 0) / 100) * usableHeight);
};

const buildPath = (trendData, key) => trendData
  .map((point, index) => `${index === 0 ? 'M' : 'L'} ${getX(index, trendData.length)} ${getY(point[key])}`)
  .join(' ');

const RetentionRateCard = ({ retentionRate, isLoading = false, yearOverYearComparison = false }) => {
  if (isLoading) {
    return (
      <div className="companion-report-card page-break-avoid h-100">
        <Skeleton width="180px" height="20px" className="mb-3" />
        <div className="companion-retention-summary-grid mb-4">
          {[1, 2, 3].map((index) => (
            <Skeleton key={index} height="78px" />
          ))}
        </div>
        <Skeleton height="230px" />
      </div>
    );
  }

  if (!retentionRate) {
    return null;
  }

  const currentPeriod = retentionRate.currentPeriod || {};
  const trendData = retentionRate.trendData || [];
  const summaryMetrics = [
    { label: 'Day 1', value: currentPeriod.day1Percent, change: currentPeriod.day1ChangePercent },
    { label: 'Day 7', value: currentPeriod.day7Percent, change: currentPeriod.day7ChangePercent },
    { label: 'Day 30', value: currentPeriod.day30Percent, change: currentPeriod.day30ChangePercent }
  ];
  const yAxisTicks = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];

  return (
    <div className="companion-report-card page-break-avoid h-100">
      <div className="companion-card-title-row">
        <div className="d-flex align-items-center">
          <h2 className="companion-card-title">Retention Rate (%)</h2>
          <OverlayTrigger
            placement="top"
            overlay={(
              <Tooltip id="retention-rate-card-tooltip" className="custom-tooltip">
                <h6>Retention Rate (%)</h6>
                <span className="mb-0">
                  Percentage of users returning after 1, 7, and 30 days in the selected reporting period.
                </span>
              </Tooltip>
            )}
          >
            <FontAwesomeIcon className="circle-question mx-1" icon={faCircleQuestion} />
          </OverlayTrigger>
        </div>
      </div>

      <div className="companion-retention-summary-grid">
        {summaryMetrics.map((metric) => {
          const isPositive = Number(metric.change || 0) > 0;
          return (
            <div className="companion-retention-metric" key={metric.label}>
              <p className="companion-retention-label">{metric.label}</p>
              <strong>{formatPercent(metric.value)}</strong>
              {yearOverYearComparison && (
                <span className={isPositive ? 'companion-change-positive' : 'companion-change-negative'}>
                  {formatChange(metric.change)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="companion-chart-wrap" aria-label="Retention rate trend chart">
        <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img">
          {yAxisTicks.map((tick) => {
            const y = getY(tick);
            return (
              <g key={tick}>
                <line
                  x1={PADDING.left}
                  x2={CHART_WIDTH - PADDING.right}
                  y1={y}
                  y2={y}
                  className="companion-chart-grid-line"
                />
                <text x="4" y={y + 4} className="companion-chart-axis-label">{tick}</text>
              </g>
            );
          })}

          {trendData.map((point, index) => {
            const x = getX(index, trendData.length);
            return (
              <g key={point.weekLabel}>
                <line
                  x1={x}
                  x2={x}
                  y1={PADDING.top}
                  y2={CHART_HEIGHT - PADDING.bottom}
                  className="companion-chart-vertical-line"
                />
                <text
                  x={x}
                  y={CHART_HEIGHT - 12}
                  textAnchor="middle"
                  className="companion-chart-axis-label"
                >
                  {point.weekLabel}
                </text>
              </g>
            );
          })}

          {SERIES.map((series) => (
            <g key={series.key}>
              <path
                d={buildPath(trendData, series.key)}
                fill="none"
                stroke={series.color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {trendData.map((point, index) => (
                <circle
                  key={`${series.key}-${point.weekLabel}`}
                  cx={getX(index, trendData.length)}
                  cy={getY(point[series.key])}
                  r="4"
                  fill={series.color}
                  stroke="#ffffff"
                  strokeWidth="2"
                />
              ))}
            </g>
          ))}
        </svg>
      </div>

      <div className="companion-chart-legend">
        {SERIES.map((series) => (
          <span key={series.key}>
            <i style={{ borderColor: series.color, backgroundColor: `${series.color}22` }}></i>
            <span className='mx-2'>{series.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

RetentionRateCard.propTypes = {
  retentionRate: PropTypes.shape({
    currentPeriod: PropTypes.shape({
      day1Percent: PropTypes.number,
      day7Percent: PropTypes.number,
      day30Percent: PropTypes.number,
      day1ChangePercent: PropTypes.number,
      day7ChangePercent: PropTypes.number,
      day30ChangePercent: PropTypes.number
    }),
    trendData: PropTypes.arrayOf(PropTypes.shape({
      weekLabel: PropTypes.string,
      day1Percent: PropTypes.number,
      day7Percent: PropTypes.number,
      day30Percent: PropTypes.number
    }))
  }),
  isLoading: PropTypes.bool,
  yearOverYearComparison: PropTypes.bool
};

export default RetentionRateCard;